import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Switch, FormControlLabel, TextField,
  Select, MenuItem, FormControl, InputLabel, Button, Alert, useTheme,
  Chip, InputAdornment, Divider, Skeleton,
} from '@mui/material';
import { Warning, InfoOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import lateFeePolicyService, {
  LateFeeKind, LateFeePolicy,
} from '../../services/lateFeePolicyService';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useAuth } from '../../context/AuthContext';

/**
 * Late Fee Policy — one-doc-per-school configuration for automated late fees.
 * Kept intentionally lean: enable/grace/kind/amount/cap. Reminders and
 * multi-step escalations are out of scope for v1.
 */

const KIND_LABEL: Record<LateFeeKind, string> = {
  flat:          'Flat amount',
  percent:       'Percent of net',
  daily_flat:    'Flat, per day overdue',
  daily_percent: 'Percent of net, per day overdue',
};

const KIND_HINT: Record<LateFeeKind, string> = {
  flat:          'A single amount added the moment the invoice crosses the grace period.',
  percent:       'A percentage of the invoice net, added once past grace.',
  daily_flat:    'Flat amount charged for every additional day past grace. Capped by ceiling.',
  daily_percent: 'Percentage of the net charged for every additional day past grace. Capped by ceiling.',
};

const LateFeePolicySettings: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const { currency } = useAuth();
  // Settings page uses its own scope so admins can grant "collect payments"
  // (`fees.write`) without also handing over control of the fine formula.
  const hasWrite = canWrite('settings_late_fee_policy');

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [initial, setInitial]     = useState<LateFeePolicy | null>(null);

  const [enabled, setEnabled]     = useState(false);
  const [dueDays, setDueDays]     = useState<number>(10);
  const [graceDays, setGraceDays] = useState<number>(7);
  const [kind, setKind]           = useState<LateFeeKind>('flat');
  const [amount, setAmount]       = useState<string>('0');
  const [cap, setCap]             = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const p = await lateFeePolicyService.getLateFeePolicy();
        if (cancelled) return;
        setInitial(p);
        setEnabled(p.enabled);
        setDueDays(p.dueDays);
        setGraceDays(p.graceDays);
        setKind(p.kind);
        setAmount(String(p.amount ?? 0));
        setCap(p.cap == null ? '' : String(p.cap));
      } catch (e: any) {
        if (!cancelled) enqueueSnackbar(e?.message || 'Failed to load policy', { variant: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enqueueSnackbar]);

  const isPercentKind = kind === 'percent' || kind === 'daily_percent';
  const isDailyKind   = kind === 'daily_flat' || kind === 'daily_percent';

  // `currency` from useAuth is guaranteed non-empty (the context supplies a
  // PKR fallback centrally), so consumers use it directly.
  const amountAdornment = isPercentKind ? '%' : currency;
  const capAdornment = currency;

  const dirty = useMemo(() => {
    if (!initial) return false;
    return (
      enabled   !== initial.enabled ||
      dueDays   !== initial.dueDays ||
      graceDays !== initial.graceDays ||
      kind      !== initial.kind ||
      Number(amount) !== Number(initial.amount) ||
      (cap === '' ? null : Number(cap)) !== initial.cap
    );
  }, [initial, enabled, dueDays, graceDays, kind, amount, cap]);

  const validate = (): string | null => {
    if (!Number.isFinite(Number(graceDays)) || Number(graceDays) < 0 || Number(graceDays) > 180) {
      return 'Grace days must be 0–180';
    }
    if (!Number.isFinite(Number(amount)) || Number(amount) < 0) {
      return 'Amount must be a non-negative number';
    }
    if (isPercentKind && Number(amount) > 100) {
      return 'Percent amount must be between 0 and 100';
    }
    if (cap !== '' && (!Number.isFinite(Number(cap)) || Number(cap) < 0)) {
      return 'Cap must be a non-negative number';
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) {
      enqueueSnackbar(err, { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const next = await lateFeePolicyService.updateLateFeePolicy({
        enabled,
        dueDays,
        graceDays: Number(graceDays),
        kind,
        amount: Number(amount),
        cap: cap === '' ? null : Number(cap),
      });
      setInitial(next);
      enqueueSnackbar('Due dates and late fees saved', { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to save policy', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Live preview of the fee for a hypothetical 10,000 invoice, 10 days past
  // grace. Kept above the `loading` early-return so the hook ordering is
  // identical on every render (React's rules-of-hooks).
  const example = useMemo(() => {
    const netInvoice = 10000;
    const daysOverdue = 10;
    let charge = 0;
    if (kind === 'flat') charge = Number(amount);
    else if (kind === 'percent') charge = (Number(amount) / 100) * netInvoice;
    else if (kind === 'daily_flat') charge = Number(amount) * daysOverdue;
    else if (kind === 'daily_percent') charge = (Number(amount) / 100) * netInvoice * daysOverdue;
    if (cap !== '' && Number(cap) > 0) charge = Math.min(charge, Number(cap));
    return Math.round(charge * 100) / 100;
  }, [kind, amount, cap]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rounded" height={140} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* The page around this panel carries its title. */}
      <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        Late fees are added only when someone clicks <strong>Apply late fees</strong> on the Challans page. Nothing is charged automatically.
      </Alert>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label="Due after issue"
              type="number"
              value={dueDays}
              onChange={e => setDueDays(Math.max(0, Math.min(90, parseInt(e.target.value || '0', 10))))}
              inputProps={{ min: 0, max: 90, step: 1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
              disabled={!hasWrite || saving}
              sx={{ width: 200 }}
            />
            <Typography variant="body2" color="text.secondary">
              Monthly challans fall due this many days after their issue date, unless a due date is picked when generating them.
            </Typography>
          </Stack>

          <Divider />

          {/* Enable */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(_, v) => setEnabled(v)}
                  disabled={!hasWrite || saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Charge late fees</Typography>
                  <Typography variant="caption" color="text.secondary">
                    When off, the <em>Apply late fees</em> button is hidden across the app.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'center' }}
            />
          </Box>

          <Divider />

          {/* Grace */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Grace days"
              type="number"
              value={graceDays}
              onChange={e => setGraceDays(Math.max(0, Math.min(180, Number(e.target.value) || 0)))}
              inputProps={{ min: 0, max: 180, step: 1 }}
              disabled={!hasWrite || saving || !enabled}
              helperText="Days after the due date before a late fee attaches. 0 = same day."
              sx={{ minWidth: 200 }}
            />

            {/* Kind */}
            <FormControl sx={{ flex: 1, minWidth: 240 }} disabled={!hasWrite || saving || !enabled}>
              <InputLabel>Charge type</InputLabel>
              <Select
                label="Charge type"
                value={kind}
                onChange={e => setKind(e.target.value as LateFeeKind)}
              >
                {(Object.keys(KIND_LABEL) as LateFeeKind[]).map(k => (
                  <MenuItem key={k} value={k}>{KIND_LABEL[k]}</MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                {KIND_HINT[kind]}
              </Typography>
            </FormControl>
          </Stack>

          {/* Amount + Cap */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={isPercentKind ? 'Percent' : 'Amount'}
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              inputProps={{ min: 0, step: isPercentKind ? 0.1 : 100 }}
              disabled={!hasWrite || saving || !enabled}
              InputProps={{
                endAdornment: <InputAdornment position="end">{amountAdornment}</InputAdornment>,
              }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Cap per challan (optional)"
              type="number"
              value={cap}
              onChange={e => setCap(e.target.value)}
              inputProps={{ min: 0, step: 100 }}
              placeholder="No cap"
              disabled={!hasWrite || saving || !enabled}
              InputProps={{
                endAdornment: <InputAdornment position="end">{capAdornment}</InputAdornment>,
              }}
              helperText={isDailyKind ? 'Recommended for daily charges — keeps the tally bounded.' : 'Optional ceiling per invoice.'}
              sx={{ flex: 1 }}
            />
          </Stack>

          {/* Live example */}
          {enabled && (
            <Alert
              severity="info"
              variant="outlined"
              icon={<InfoOutlined fontSize="small" />}
              sx={{ borderRadius: 1.5 }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2">
                  <strong>Example:</strong> a {currency} 10,000 invoice, 10 days past grace →
                </Typography>
                <Chip
                  size="small"
                  label={`${currency} ${example.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                  color="warning"
                  variant="filled"
                  sx={{ height: 24, fontWeight: 700 }}
                />
                <Typography variant="body2" color="text.secondary">late fee</Typography>
              </Stack>
            </Alert>
          )}

          {!enabled && (
            <Alert severity="warning" variant="outlined" icon={<Warning fontSize="small" />}>
              Policy is currently disabled. Enable it to configure and use late fees.
            </Alert>
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button
            onClick={save}
            variant="contained"
            disableElevation
            disabled={!hasWrite || saving || !dirty}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LateFeePolicySettings;
